import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, X, Quote } from "lucide-react";
import { LINKS, ASSETS } from "../lib/constants";

const SectionLabel = ({ children, dark = false, onYellow = false }) => (
  <span className={`section-label ${dark ? "on-dark" : ""} ${onYellow ? "on-yellow" : ""}`}>{children}</span>
);

const Card = ({ num, title, body, dark = false }) => (
  <div className={`p-6 rounded-[10px] border-[1.5px] transition-all duration-300 hover:-translate-y-1 ${dark ? "border-brand-yellow bg-white/[0.04] hover:shadow-[6px_6px_0_#F5C632]" : "border-brand-black bg-white hover:shadow-[6px_6px_0_#0A0A0A]"}`}>
    <span className={`text-xs uppercase tracking-[0.18em] font-bold ${dark ? "text-brand-yellow" : "text-brand-stone"}`}>{num}</span>
    <h4 className={`font-display text-xl mt-3 mb-2 leading-tight uppercase ${dark ? "text-white" : ""}`}>{title}</h4>
    <p className={`text-[14px] leading-relaxed ${dark ? "text-white/85" : "text-brand-ink"}`}>{body}</p>
  </div>
);

const FAQ_ITEMS = [
  { q: "Do I need perfect numbers ready before the session?", a: "No. You'll complete a simple intake form before the call, and that gives us enough to start with. The more honest and detailed you are the better though, as I'll be analysing this before our session together. If some details are missing, that's useful too — it shows us where visibility needs to improve. There is no judgement, ever." },
  { q: "Is this bookkeeping?", a: "No. Bookkeeping records what has happened. This session helps you interpret what is happening and what it means for your next business decision." },
  { q: "What if I feel embarrassed about my numbers?", a: "That's very normal — and I've been there myself. Many capable founders avoid their numbers because the process has felt confusing, clinical, or shame-heavy in the past. This is a clarity space, not a judgement space." },
  { q: "Will I leave with a full financial plan?", a: "You'll leave with clearer visibility and a focused next direction. This is a 90-minute diagnostic strategy session — it's not designed to replace ongoing financial strategy support." },
  { q: "Is this right for me if I already have an accountant?", a: "Yes — if your accountant handles compliance but you still feel unclear about business decisions. This session sits in the gap between the numbers and the decision." },
  { q: "What happens after the session?", a: "You'll have a clearer view of your financial picture and your next practical focus. Around 60 days after our session, I'll reach out with a free 30-minute check-in — just for accountability. No pressure. No obligation." },
  { q: "What if I want ongoing support?", answerHtml: 'My <strong>Profit &amp; Cashflow Advisory</strong> may be the better fit. You can <a href="/advisory" class="underline decoration-brand-yellow decoration-2 underline-offset-2 font-semibold">explore advisory</a> to find out more, or <a href="https://schedule.profitmind.co.uk/free-20min-chat" target="_blank" rel="noopener noreferrer" class="underline decoration-brand-yellow decoration-2 underline-offset-2 font-semibold">book a free chat</a> to talk through it.' },
  { q: "Not quite ready yet — what do you suggest?", a: "Start with the free Profitable-But-Broke Reality Check or join the forecastly waitlist." },
];

function FAQItem({ item, isOpen, onToggle }) {
  return (
    <div className={`border-[1.5px] rounded-md p-4 transition-colors duration-200 ${isOpen ? "border-brand-black bg-brand-yellow" : "border-brand-black bg-white hover:bg-brand-yellow/20"}`}>
      <button type="button" onClick={onToggle} className="w-full text-left cursor-pointer font-semibold text-brand-black flex items-center justify-between gap-4" data-testid={`faq-toggle-${item.q.slice(0,20).replace(/\s+/g,'-')}`}>
        <span>{item.q}</span>
        <span className={`font-display text-2xl leading-none transition-transform duration-200 shrink-0 ${isOpen ? "rotate-45 text-brand-black" : "text-brand-yellow"}`}>+</span>
      </button>
      {isOpen && (
        item.answerHtml ? (
          <p className="mt-3 text-[15px] leading-relaxed text-brand-black" dangerouslySetInnerHTML={{ __html: item.answerHtml }} />
        ) : (
          <p className="mt-3 text-[15px] leading-relaxed text-brand-black">{item.a}</p>
        )
      )}
    </div>
  );
}

export default function MoneyFlowSession() {
  const [openFaq, setOpenFaq] = useState(-1);

  return (
    <div data-testid="mfs-page">
      {/* HERO */}
      <section className="bg-white pt-12 pb-16 lg:pt-16 lg:pb-20">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 items-center reveal">
            <div className="lg:col-span-7">
              <SectionLabel>Money Flow Strategy Session</SectionLabel>
              <h1 className="font-display text-[clamp(2.4rem,5.6vw,4.8rem)] mt-6 leading-[0.92]">
                You're making money.<br />
                Why does it still feel <span className="hi-yellow">so unclear?</span>
              </h1>
              <p className="mt-7 text-lg lg:text-xl leading-relaxed text-brand-ink max-w-3xl">
                A focused 90-minute profit and cashflow strategy session — <strong>Part 2 of Follow The
                Cash™ Method</strong> — for purpose-led business owners who want to understand what's
                working, what's leaking, and what decision needs to come next.
              </p>
              <p className="mt-6 text-[17px] leading-relaxed text-brand-ink max-w-3xl">
                You are making money. You are holding a bigger vision. But behind the scenes, the
                numbers still feel harder than they should. This session, with me by your side, gives
                you a calm place to look properly, understand the financial reality of your business —
                so you can stop guessing, stop firefighting, and make your next move with clarity and
                more confidence.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <a href={LINKS.moneyFlowStripe} target="_blank" rel="noopener noreferrer" className="btn-yellow" data-testid="mfs-hero-book-btn">
                  Book your session — £197 <ArrowUpRight size={18} />
                </a>
                <a href="#mfs-included" className="btn-secondary" data-testid="mfs-hero-included-btn">
                  See what's included
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] max-w-[460px] mx-auto lg:ml-auto">
                <div className="absolute inset-0 translate-x-3 translate-y-3 bg-brand-yellow rounded-[10px]" />
                <div className="absolute inset-0 -translate-x-1 -translate-y-1 rounded-[10px] overflow-hidden border-[3px] border-brand-black">
                  <img src={ASSETS.moneyFlowHero} alt="Magdalena Mahdy — Profit and Cashflow Strategist" className="w-full h-full object-cover" data-testid="mfs-hero-portrait" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE QUIET QUESTIONS */}
      <section className="bg-brand-yellow py-20 lg:py-24 border-y-[1.5px] border-brand-black">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel onYellow>The quiet questions</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95]">
            The Profitable-But-Broke reality.
          </h2>
          <p className="mt-4 font-display text-xl sm:text-2xl normal-case uppercase leading-[1.35] text-brand-black">
            You're <span className="bg-white px-2">Profitable But Broke</span>. This session is where we find out why.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "Can I actually afford to hire?",
              "Which offer is really profitable?",
              "Why am I still not paying myself consistently?",
              "Where is the money going each month?",
              "Is this growth sustainable, or am I just working harder?",
              "What should I focus on first?",
            ].map((q, i) => (
              <div key={i} className="bg-white border-[1.5px] border-brand-black rounded-[10px] p-5 flex items-start gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_#0A0A0A]">
                <span className="font-display text-4xl text-brand-black leading-none shrink-0">?</span>
                <p className="text-[15px] leading-relaxed text-brand-ink self-center">{q}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-lg leading-relaxed text-brand-black">
            On the outside, you may look calm and capable. Underneath, there can be a low hum of
            financial pressure and anxiety that most people never see. This session is where we
            bring that pressure and anxiety into the light, gently and practically.
          </p>

          <p className="mt-8 font-display text-xl sm:text-2xl normal-case leading-[1.4] uppercase text-brand-black">
            "This is not a personal failure. It is a visibility problem. When you do not have a
            clear money map, every decision feels bigger, riskier and more emotional than it needs
            to be."
          </p>
        </div>
      </section>

      {/* THE SHIFT */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel>The shift</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95]">
            More revenue is not the same as <span className="hi-yellow">more safety.</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-brand-ink">
            Most small business owners think the next income milestone will fix the fog. One more
            client. One more launch. One more strong month. But more sales can create more pressure
            when profit, pay, capacity and cashflow are unclear. This session helps you shift from
            reactive money management into calmer profit and cashflow decisions.
          </p>

          <div className="mt-10 grid md:grid-cols-2 rounded-[10px] border-[1.5px] border-brand-black overflow-hidden">
            <div className="p-8 md:border-r-[1.5px] border-brand-black/15">
              <h4 className="font-display text-xl uppercase mb-4">Reactive money management</h4>
              <ul className="space-y-2.5 text-[15px] leading-relaxed text-brand-ink">
                {[
                  "Checking the bank balance for reassurance",
                  "Chasing sales without knowing profit",
                  "Avoiding the numbers until something feels urgent",
                  "Paying yourself from what is left",
                  "Delaying decisions because the picture feels unclear",
                ].map((x) => <li key={x} className="flex gap-2.5"><X size={16} className="text-brand-stone mt-1 shrink-0" />{x}</li>)}
              </ul>
            </div>
            <div className="p-8 bg-brand-yellow">
              <h4 className="font-display text-xl uppercase mb-4">Calm profit and cashflow decisions</h4>
              <ul className="space-y-2.5 text-[15px] leading-relaxed text-brand-black">
                {[
                  "Understanding what is really happening",
                  "Knowing which offers truly support the business",
                  "Seeing where money is leaking — and stopping it",
                  "Paying yourself with intention",
                  "Making decisions from evidence, not anxiety",
                ].map((x) => <li key={x} className="flex gap-2.5"><Check size={16} className="text-brand-black mt-1 shrink-0" strokeWidth={3} />{x}</li>)}
              </ul>
            </div>
          </div>

          <p className="mt-10 text-[17px] leading-relaxed text-brand-ink">
            You do not need to become a finance person to make better money decisions. You need the
            numbers translated into clear business direction.
          </p>

          <p className="mt-8 font-display text-xl sm:text-2xl normal-case uppercase leading-[1.4] text-brand-black">
            This session is <span className="hi-yellow">Part 2 of the Follow The Cash™ method: Start Knowing.</span>{" "}
            You stop guessing. You start seeing what the numbers actually say — so your next move
            comes from evidence, not anxiety.
          </p>
        </div>
      </section>

      {/* INTRODUCING THE MONEY FLOW STRATEGY SESSION */}
      <section className="bg-brand-stone-50 py-20 lg:py-28 border-y-[1.5px] border-brand-black/15" data-testid="mfs-introducing">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <SectionLabel>Introducing the Money Flow Strategy Session</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4.2rem)] mt-6 leading-[0.95]">
            A calm, practical Money Flow <span className="hi-yellow">Strategy Session.</span>
          </h2>

          <div className="relative mt-14 group">
            <div className="absolute inset-0 translate-x-3 translate-y-3 bg-brand-black rounded-[10px] transition-transform duration-300 group-hover:translate-x-4 group-hover:translate-y-4" aria-hidden="true" />
            <div className="relative bg-white border-[1.5px] border-brand-black rounded-[10px] p-8 sm:p-12 text-left">
              <p className="text-lg leading-relaxed text-brand-ink text-center">
                Together, we review the current financial reality of your business and map your
                clearest next move. In the 90 minutes, we look at the areas that quietly shape your
                confidence as a business owner:
              </p>
              <div className="mt-7 flex flex-wrap gap-3 justify-center">
                {["Revenue rhythm", "Offer profitability", "Expenses", "Personal pay", "Decision readiness"].map((l) => (
                  <span key={l} className="inline-flex items-center gap-2 px-4 py-2 bg-brand-yellow border-[1.5px] border-brand-black rounded-md text-sm font-bold uppercase tracking-wide">{l}</span>
                ))}
              </div>
              <p className="mt-8 text-[17px] leading-relaxed text-brand-ink text-center">
                You walk away with a clear <strong>30/60/90-day action plan</strong> and a complimentary{" "}
                <strong>30-minute follow-up check-in after 60 days</strong>, so the work keeps landing
                after the session ends. You also receive a <strong>discount code for forecastly</strong>,
                my cashflow forecasting software, so you can keep the clarity rolling.
              </p>
              <p className="mt-5 text-[17px] leading-relaxed text-brand-ink text-center">
                This is not bookkeeping or compliance. It is not someone handing you a generic
                spreadsheet and expecting you to make sense of it alone. This is strategic
                interpretation in plain English — so you can understand what matters, what is
                leaking and what needs attention first.
              </p>
              <div className="mt-9 flex justify-center">
                <a href={LINKS.moneyFlowStripe} target="_blank" rel="noopener noreferrer" className="btn-yellow" data-testid="mfs-introducing-book-btn">
                  Book your Money Flow Strategy Session <ArrowUpRight size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NOTE FROM MAGDALENA */}
      <section className="bg-brand-black text-white py-20 lg:py-24" data-testid="mfs-note">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <div className="relative aspect-square max-w-[360px] mx-auto rounded-[10px] overflow-hidden border-[3px] border-brand-yellow">
              <img src={ASSETS.coffeeLaptop} alt="Magdalena working calmly with coffee and laptop" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <SectionLabel dark>A note from Magdalena</SectionLabel>
            <Quote size={40} className="text-brand-yellow mt-4" strokeWidth={1.5} />
            <p className="font-display text-3xl sm:text-4xl leading-tight mt-3 mb-6">
              "Confusion isn't failure. It's simply unstructured data."
            </p>
            <p className="text-[17px] leading-relaxed max-w-2xl">
              You're not behind. And you're also not alone. You're a capable CEO who simply needs
              your numbers translated — not judged. That's the whole point of this session.
              Let's do it together.
            </p>
          </div>
        </div>
      </section>

      {/* FIVE LENSES */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel>Part 2 of Follow The Cash™: Start Knowing</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95]">
            Five diagnostic lenses. One <span className="hi-yellow">cleaner picture.</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-brand-ink">
            The Money Flow Strategy Session zooms in on Part 2 — Start Knowing — to give you a
            clean diagnostic of where you are right now. From here, you'll know exactly which part
            of Follow The Cash™ to focus on next.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card num="01" title="Revenue rhythm" body="Is your income steady, spiky, seasonal, or dependent on effort surges?" />
            <Card num="02" title="Offer profitability" body="Which offers are actually supporting the business, and which ones may be draining time, margin, or energy?" />
            <Card num="03" title="Expense leakage" body="Where is money leaving quietly through outdated tools, unclear support costs, or spending that no longer fits this stage?" />
            <Card num="04" title="Personal pay" body="Is the business supporting your life, or are you still paying yourself from leftovers?" />
            <Card num="05" title="Decision readiness" body="What can you safely decide next, and what needs more clarity before you move?" />
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section id="mfs-included" className="bg-brand-yellow py-20 lg:py-24 border-y-[1.5px] border-brand-black">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel onYellow>What's included</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95]">
            Everything inside your 90 minutes.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card num="01" title="Pre-session intake snapshot" body="You complete a simple form before we meet so we can use the session for strategy, not admin." />
            <Card num="02" title="90-minute online video strategy session" body="We walk through your current financial picture together and focus on what needs clarity now." />
            <Card num="03" title="Revenue + offer review" body="We look at how money is coming in and which offers may need attention." />
            <Card num="04" title="Expense + personal pay check-in" body="We identify where money may be leaking and whether your personal pay needs more structure." />
            <Card num="05" title="Decision clarity discussion" body="We focus on the decision that feels most pressing — hiring, pricing, investing, simplifying or scaling." />
            <Card num="06" title="30/60/90-day action plan" body="You leave with a clear, focused plan for the next 30, 60 and 90 days — not a new pile of half-formed tasks." />
            <Card num="07" title="Free 30-min check-in" body="A complimentary follow-up after 60 days to see how things are landing — no pressure, no obligation, just continued support." />
            <Card num="08" title="Forecastly discount" body="A discount code to forecastly, your 'Take Control' tool (Part 3 of Follow The Cash™) — so the clarity from this session turns into action." />
            <Card num="09" title="Optional session recording" body="If you'd like, we record the session so you can revisit it anytime — no rush to capture every note in the moment." />
          </div>
        </div>
      </section>

      {/* OUTCOME */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionLabel>The outcome</SectionLabel>
            <h2 className="font-display text-[clamp(2.2rem,5vw,3.6rem)] mt-6 leading-[0.95]">
              What changes when you can finally <span className="hi-yellow">see the pattern.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg leading-relaxed text-brand-ink">
              The goal isn't for you to leave with more tabs open in your brain. The goal is for
              you to breathe differently. When the numbers stop feeling like a threat and start
              becoming useful information, your decisions change. <strong>You breathe better. Your
              nervous system relaxes.</strong> You stop trying to fix everything at once. You stop
              relying on one strong sales month to feel safe. You stop carrying every financial
              question in your head.
            </p>
            <p className="mt-6 font-display text-2xl uppercase leading-[1.3]">
              You don't need more pressure. You need a <span className="hi-yellow">clearer path.</span>
            </p>
            <ul className="mt-6 space-y-2 text-[15px] text-brand-ink">
              {[
                "A clearer view of what's working financially",
                "A calmer understanding of what needs attention first",
                "Insight into which offer, expense, or decision is creating fog",
                "A practical 30/60/90-day action plan",
                "A free 30-min follow-up check-in after 60 days",
                "More confidence in your next money decision",
                "The feeling of not holding it all alone anymore",
              ].map((x) => <li key={x} className="flex gap-2.5"><Check size={17} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />{x}</li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* FIT */}
      <section className="bg-brand-stone-50 py-20 lg:py-24 border-y-[1.5px] border-brand-black/15">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <h2 className="font-display text-[clamp(2rem,4vw,3rem)] leading-[0.95] text-center">Is this the right fit?</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="bg-white border-[1.5px] border-brand-black rounded-[10px] p-7">
              <h4 className="font-display text-xl uppercase mb-4">This is for you if…</h4>
              <ul className="space-y-2 text-[15px] text-brand-ink">
                {[
                  "Your business has momentum, but your financial clarity feels behind.",
                  "You're making money — but you're unsure what's truly profitable.",
                  "You want to scale without creating more pressure.",
                  "You're tired of checking your bank balance as your main source of reassurance.",
                  "You want to pay yourself with more consistency.",
                  "You have a decision on your mind — hiring, pricing, investing, simplifying, changing an offer.",
                  "You want financial support that feels calm, human, and practical.",
                ].map((x) => <li key={x} className="flex gap-2.5"><Check size={17} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />{x}</li>)}
              </ul>
            </div>
            <div className="bg-brand-black text-white border-[1.5px] border-brand-black rounded-[10px] p-7">
              <h4 className="font-display text-xl uppercase mb-4">This isn't for you if…</h4>
              <ul className="space-y-2 text-[15px] text-white/90">
                {[
                  "You want someone to do your bookkeeping.",
                  "You only want tax advice.",
                  "You're not willing to look honestly at the current numbers.",
                  "You want a magic answer without giving business context.",
                  "You want to keep pretending that more sales will automatically solve everything.",
                  "You're looking for a full long-term finance department right now.",
                ].map((x) => <li key={x} className="flex gap-2.5"><X size={17} className="text-brand-yellow shrink-0 mt-0.5" />{x}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INVESTMENT */}
      <section className="bg-brand-yellow py-20 lg:py-24 border-y-[1.5px] border-brand-black">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <SectionLabel onYellow>Your investment</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95]">
            One focused session. One clear map.
          </h2>
          <div className="mt-10 max-w-xl mx-auto bg-white border-[1.5px] border-brand-black rounded-[10px] p-9 text-left">
            <p className="text-xs uppercase tracking-[0.2em] text-brand-stone font-bold mb-3 text-center">Launch price</p>
            <p className="font-display text-7xl leading-none text-center">£197</p>
            <p className="mt-3 text-sm uppercase tracking-[0.15em] font-bold text-center">90-minute strategy session</p>
            <ul className="mt-8 space-y-2.5 text-[15px] text-brand-ink border-t-2 border-brand-black/10 pt-6">
              {[
                "Pre-session intake snapshot",
                "90-minute online video strategy session",
                "Focused review of revenue, offers, expenses, personal pay & decision clarity",
                "Clear 30/60/90-day action plan",
                "Free 30-min follow-up check-in after 60 days",
                "Discount code to forecastly, my cashflow forecasting software",
                "Optional session recording",
              ].map((x) => <li key={x} className="flex gap-2.5"><Check size={17} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />{x}</li>)}
            </ul>
            <a href={LINKS.moneyFlowStripe} target="_blank" rel="noopener noreferrer" className="btn-primary shadow-w-default mt-8 w-full justify-center" data-testid="mfs-invest-book-btn">
              Book your Money Flow Strategy Session <ArrowUpRight size={18} />
            </a>
          </div>
          <p className="mt-8 text-[15px] leading-relaxed text-brand-black max-w-2xl mx-auto">
            <strong>Limited to 5 bookings per month at this rate.</strong> This launch price is
            available while I refine the first round of Money Flow Strategy Sessions as part of the
            ProfitMIND evolution. As the offer develops, the price will increase.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel>Questions you might be asking</SectionLabel>
          <h2 className="font-display text-[clamp(2rem,4vw,3rem)] mt-6 leading-[0.95] mb-10">
            A few <span className="hi-yellow">honest answers.</span>
          </h2>
          <div className="space-y-3" data-testid="mfs-faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <FAQItem key={i} item={item} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-black py-20 lg:py-24" data-testid="mfs-final-cta">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="rounded-[10px] border-[1.5px] border-brand-yellow bg-white/[0.03] p-8 sm:p-12 text-center text-white">
            <h2 className="font-display text-[clamp(2rem,4.4vw,3.8rem)] leading-[0.95] mx-auto max-w-3xl">
              Your next move is not to <span className="text-brand-yellow">figure it out alone</span>, again.
            </h2>
            <p className="mt-7 text-lg leading-relaxed mx-auto max-w-2xl">
              You have carried the mental load for long enough. The pricing questions. The hiring
              decisions. The late-night calculations. The quiet hope that the next strong sales
              month will finally make everything feel safer.
            </p>
            <p className="mt-4 text-lg leading-relaxed">
              One focused session. One clearer map. One calmer next step.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <a href={LINKS.moneyFlowStripe} target="_blank" rel="noopener noreferrer" className="btn-yellow btn-yellow-on-dark" data-testid="mfs-final-book-btn">
                Book my Money Flow Strategy Session <ArrowUpRight size={18} />
              </a>
              <a href={LINKS.tidycal} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 border-[1.5px] border-white text-white text-sm font-bold uppercase tracking-wide rounded-md hover:bg-white hover:text-brand-black transition-colors" data-testid="mfs-final-chat-btn">
                Or book a free 20-min chat
              </a>
            </div>
            <p className="mt-9 text-sm uppercase tracking-[0.18em] font-bold text-brand-yellow">
              Part 2 of Follow The Cash™. Start Knowing what your numbers actually say.
            </p>
            <p className="mt-6 text-xs text-white/70">
              Not sure yet? <Link to={LINKS.followTheCash} className="underline underline-offset-2">Explore the full Follow The Cash™ method</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
